import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import * as React from "react";

import { useBackCloses } from "@/components/ui/back-closes";
import { Button } from "@/components/ui/button";
import { DrawerGrabber, useDrawerDrag } from "@/components/ui/drawer-grabber";
import { keyboardDrawerStyle, useVirtualKeyboardInset } from "@/components/ui/virtual-keyboard";
import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";

function Sheet({
  backCloses,
  actionsRef: outerActionsRef,
  ...props
}: SheetPrimitive.Root.Props & {
  /** Browser Back closes this sheet (see `back-closes.tsx`). Undefined = inherit from
   *  `<BackClosesOverlays>`; pass `false` for a sheet whose open state lives in the URL. */
  backCloses?: boolean;
}) {
  const ownActionsRef = React.useRef<SheetPrimitive.Root.Actions | null>(null);
  const actionsRef = outerActionsRef ?? ownActionsRef;
  // Base UI's imperative close runs the caller's onOpenChange(false) like any other dismissal.
  useBackCloses(backCloses, props.open === true && !!props.onOpenChange, () =>
    actionsRef.current?.close(),
  );
  return <SheetPrimitive.Root data-slot="sheet" actionsRef={actionsRef} {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 touch-none bg-black/10 duration-100 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  grabberOverlay = false,
  clearBackdrop = false,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
  /** Float the grabber ON TOP of the content (absolute) instead of reserving a strip above
   *  it — lets a full-bleed child reach the very top, with the grabber (and X) overlaid. */
  grabberOverlay?: boolean;
  /** No dim and no blur behind the sheet (it still closes on an outside tap) — for a sheet
   *  whose job is to change what's behind it, e.g. the composer's background picker. */
  clearBackdrop?: boolean;
}) {
  // Drag-to-dismiss for bottom sheets — on touch the WHOLE PANEL drags (yielding to any
  // scrollable under the finger that isn't at its top); with a mouse only the grabber
  // does. Past the threshold it fires this hidden Close. Only wired for side="bottom".
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const { panelProps, grabberProps } = useDrawerDrag(closeRef);
  const isBottom = side === "bottom";
  // A bottom sheet sits on top of the on-screen keyboard and shrinks to the space above
  // it (the native sheets' behaviour) — see virtual-keyboard.ts.
  const keyboardInset = useVirtualKeyboardInset(isBottom);

  return (
    <SheetPortal>
      <SheetOverlay
        className={
          clearBackdrop ? "bg-transparent supports-backdrop-filter:backdrop-blur-none" : undefined
        }
      />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          // Left/right drawers slide their FULL width (was a 2.5rem `-10` nudge + fade,
          // which read as no animation at all on the mobile hamburger menu).
          "fixed z-50 flex flex-col gap-4 overscroll-y-contain bg-background bg-clip-padding text-base shadow-lg transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-[side=bottom]:data-open:slide-in-from-bottom-full data-[side=left]:data-open:slide-in-from-left-full data-[side=right]:data-open:slide-in-from-right-full data-[side=top]:data-open:slide-in-from-top-10 data-closed:animate-out data-closed:fade-out-0 data-[side=bottom]:data-closed:slide-out-to-bottom-full data-[side=left]:data-closed:slide-out-to-left-full data-[side=right]:data-closed:slide-out-to-right-full data-[side=top]:data-closed:slide-out-to-top-10",
          className,
        )}
        {...props}
        style={{ ...props.style, ...keyboardDrawerStyle(keyboardInset) }}
        {...(isBottom ? panelProps : {})}
      >
        {isBottom && (
          <>
            <DrawerGrabber
              grabberProps={grabberProps}
              // Overlay: float on top of the content so a full-bleed child reaches the top.
              className={cn("shrink-0", grabberOverlay && "absolute inset-x-0 top-0 z-20")}
            />
            <SheetPrimitive.Close
              render={<button type="button" ref={closeRef} className="hidden" tabIndex={-1} />}
            />
          </>
        )}
        {children}
        {showCloseButton && (
          // Wrap in a positioned div (matching DialogContent): the ghost Button's
          // `hover-elevate` sets `position: relative` at a higher specificity than the
          // `absolute` utility, so `absolute` on the Button itself never wins and the X
          // falls into normal flow off-screen. A plain div has no such conflict.
          <div className="absolute top-3 right-3 z-10">
            <SheetPrimitive.Close
              data-slot="sheet-close"
              // Default X was tiny (icon-sm + a size-4 glyph); bump to a size-9 button with a
              // size-6 glyph so it's a comfortable tap target on the mobile bottom sheets.
              render={<Button variant="ghost" size="icon" />}
            >
              <XIcon className="size-6" strokeWidth={2.25} />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          </div>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("font-medium text-foreground", className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-base text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
};
